/*
 * The browser's agent loop.
 *
 * There is no browser-specific loop. This file wires the SHARED one (`@vitesse/agent-loop`) to the
 * browser's two local facts — how a request travels, and what a step is — and that is the whole of
 * it. The lifecycle underneath is the same code the server runs: the same lease and fencing epoch,
 * the same heartbeat that has to outlive a slow model call, the same authorization checkpoint before
 * a paid call, the same reconciliation of tool calls a dead driver left in an unknown state.
 *
 * That shared-ness is the point rather than a convenience. A tab is not a reliable process — it is
 * backgrounded, throttled, closed mid-run — so the browser hits every failure the server does, and
 * more often. A second implementation would drift, and the drift would show up as money spent twice
 * or a write to plant repeated, which is exactly the class the audit was for.
 *
 * WHAT THE BROWSER ADDS: nothing structural. It holds the lease while the tab is open, and hands it
 * back on close so a sweeper can pick the work up without waiting out a TTL. Close the tab mid-run
 * and the tree is durable and adoptable; reopen it and the same tab reclaims and carries on.
 */
import { HeadlessDriver, HttpDriverHost } from '@vitesse/agent-loop';
import { create, toBinary, fromBinary } from '@bufbuild/protobuf';
import { TreeViewRequestSchema, TreeViewReplySchema, OwnerActionSchema, OwnerAction_Kind } from '@vitesse/proto/vitesse/ai/v1/agent_pb.js';
/** One host for the tab. The token is read per request, not captured — a tab outlives any single
 *  session token, and a driver holding a stale one would start failing partway through a run. */
/** Wired once by the app at boot (`configureAiPlane`), because the backend origin and the session
 *  token are the app's facts, not the driver's. Everything else here is identical in every UI. */
let host = null;
export function configureDriver(aiUrl, token) {
    host = new HttpDriverHost({ baseUrl: aiUrl, token });
}
/** The configured host, for readers that are not drivers.
 *
 *  The run-tree view reads `TreeView` through the same transport a driver uses, and exporting the
 *  accessor is what keeps there being ONE host per tab: a second `HttpDriverHost` built by the view
 *  would hold its own token closure and start failing independently partway through a session. */
export const treeHost = () => requireHost();
const requireHost = () => {
    if (!host)
        throw new Error('AI driver used before configureAiPlane() — call it at boot');
    return host;
};
/**
 * Drive a tree from this tab until it is done, refused, or fenced.
 *
 * Refusal is a normal outcome. Another tab holding the lease means the work is being driven, and the
 * right response is to follow along read-only — the frames on the live channel are the same either
 * way, so a viewer and a driver see identical state.
 */
export async function runTree(opts) {
    const driver = new HeadlessDriver(requireHost(), {
        budgetUsd: opts.budgetUsd ?? 0,
        /*
         * A BACKGROUNDED TAB IS STILL A LIVE DRIVER — hold the lease long enough to prove it.
         *
         * The heartbeat renews at ttl/3, but a hidden tab has its timers THROTTLED to about once a
         * minute by the browser. Against the store's 30s default that renewal lands too late, the lease
         * lapses, and the server's adopt pass takes the tree — which for a dashboard/widget flow means
         * client-only tools (symbols.search, dashboards.update) become unreachable server stand-ins and
         * the flow degrades. Observed: switch away from the tab building a board and the whole run is
         * quietly stolen and broken.
         *
         * A 90s lease (under the store's 120s cap; heartbeat therefore ~30s) survives a ~60s throttled
         * renewal with margin, so a person who tabs away and comes back still owns their run. A tab that
         * has genuinely CLOSED fires no timers at all, so its lease still lapses — just 90s later — and
         * the adopt pass picks it up correctly. Longer, deliberately, only for the browser: the server's
         * own adopt lease stays the default.
         */
        leaseTtlMs: 90_000,
        ...(opts.onRunTerminal ? { onRunTerminal: opts.onRunTerminal } : {}),
    });
    return driver.takeover(opts.rootRunId, opts.step);
}
/**
 * Keep driving while there is work.
 *
 * `takeover` returns when it has advanced everything it could see at the moment it looked. A tree
 * whose runs spawned children during that pass has more to do, so the loop goes round again — and
 * stops the moment a pass advances nothing, is refused, or is fenced. `fenced` specifically means
 * the tree stopped being this tab's (the user stopped it, or another driver took over), and the
 * correct response is to stop rather than re-claim: re-claiming would be this tab arguing with the
 * person using it.
 */
export async function driveToCompletion(opts) {
    const passes = [];
    for (;;) {
        const report = await runTree(opts);
        passes.push(report);
        if (!report.claimed || report.fenced || report.advanced.length === 0)
            return passes;
    }
}
/**
 * Pick up work this user left running, and carry on with it.
 *
 * THIS IS THE LIVENESS STORY, and it is the browser's rather than a server's on purpose.
 *
 * A tree whose driver went away is durable and stalled: the records are intact, nothing advances,
 * and — this is the part worth being precise about — nothing is being spent. A server that adopted
 * it automatically would resume spending the user's money while they are not there, which is a
 * choice they should get to make and not a gap to be quietly filled. "Keep working while I am away"
 * is a feature with a consent question attached, not missing infrastructure.
 *
 * So the tab does it. Opening the app is the user showing up, which is the clearest possible signal
 * that they want their work continued, and it needs no cross-user index to discover anything: the
 * orphan query is scoped to the log that already belongs to them.
 *
 * (The server-side version, when a step function exists to run headlessly: a Durable Object ALARM.
 * The discovery problem that makes a cron impossible — no index of which users have stalled trees —
 * does not exist for the object that holds the tree, because it can simply schedule itself. It
 * already satisfies `DriverHost`, so adopting is `new HeadlessDriver(this, …).takeover(root, step)`.
 * The missing piece is the step, not the mechanism.)
 */
export async function resumeAbandonedWork(step, budgetUsd = 0, maxIdleMs = ORPHAN_MAX_IDLE_MS, now = Date.now) {
    const host = requireHost();
    const bytes = await host.treeView(toBinary(TreeViewRequestSchema, create(TreeViewRequestSchema, { wantOrphans: true })));
    const { orphanRootRunIds } = fromBinary(TreeViewReplySchema, bytes);
    const reports = [];
    for (const rootRunId of orphanRootRunIds) {
        // THE RUNS ARE THE EVIDENCE. The flags gate the read, so a request without `wantRuns` comes
        // back empty — and an empty tree has no "newest thing it did", which read as forgotten since
        // forever: every tree a sweep found was ended a minute after it started.
        const { runs } = fromBinary(TreeViewReplySchema, await host.treeView(toBinary(TreeViewRequestSchema, create(TreeViewRequestSchema, { rootRunId, wantRuns: true }))));
        if (orphanFate(runs, now(), maxIdleMs) === 'abandon') {
            // Ended on the record, with the reason, so it never reads as running again and nobody
            // adopts it by surprise an hour later.
            await host.ownerAct(toBinary(OwnerActionSchema, create(OwnerActionSchema, { kind: OwnerAction_Kind.ABORT, runId: rootRunId, reason: abandonedReason(maxIdleMs) })));
            continue;
        }
        // Sequentially, and refusals are expected: another tab may have adopted one between the query
        // and the claim. The lease settles that without either tab coordinating with the other.
        reports.push(...(await driveToCompletion({ rootRunId, step, budgetUsd })));
    }
    return reports;
}
/** How long a tree may sit without a driver and still be picked up as if nothing happened. */
export const ORPHAN_MAX_IDLE_MS = 10 * 60_000;
export const abandonedReason = (maxIdleMs) => `abandoned — nothing drove this for over ${Math.round(maxIdleMs / 60_000)} minutes`;
/**
 * ADOPT WHAT JUST STALLED; END WHAT WAS FORGOTTEN.
 *
 * Every unfinished tree without a driver was an orphan, however old — so the first tab to open
 * after a day away woke every tree abandoned that day, and one of them authored a model and moved
 * the app to it while the person was reading something else. Showing up is consent to continue
 * the work you were just doing; it is not consent to resume everything you ever walked away from.
 *
 * Recent by the newest thing any run in the tree did — its last headline, else when it started.
 * Pure, so the line can be tested without a host.
 */
export function orphanFate(runs, nowMs, maxIdleMs) {
    // No runs is no evidence of age, and ending work on no evidence is the one wrong answer here.
    if (runs.length === 0)
        return 'adopt';
    let newest = 0;
    for (const r of runs) {
        const at = Number((r.headlineAtNs || r.startedAtNs) / 1000000n);
        if (at > newest)
            newest = at;
    }
    return nowMs - newest > maxIdleMs ? 'abandon' : 'adopt';
}
//# sourceMappingURL=driver.js.map